export enum DocumentErrorCode {
    ERROR_MISSING_INVOICE_NUMBER = 'ERROR_MISSING_INVOICE_NUMBER',
    ERROR_INVALID_DUE_DATE = 'ERROR_INVALID_DUE_DATE',
    ERROR_INVALID_VAT_FORMAT = 'ERROR_INVALID_VAT_FORMAT',
    ERROR_INVALID_FIELD_FORMAT_INVOICE_NUMBER = 'ERROR_INVALID_FIELD_FORMAT_INVOICE_NUMBER',
    ERROR_INVOICE_NUMBER_ALREADY_USED = 'ERROR_INVOICE_NUMBER_ALREADY_USED',
    ERROR_PERIOD_CLOSED = 'ERROR_PERIOD_CLOSED',
    ERROR_BOOKYEAR_NOT_FOUND = 'ERROR_BOOKYEAR_NOT_FOUND',
    ERROR_INVALID_INVOICE_DATE = 'ERROR_INVALID_INVOICE_DATE',
    ERROR_ZERO_LINE = 'ERROR_ZERO_LINE',
    ERROR_ZERO_INVOICE = 'ERROR_ZERO_INVOICE',
    ERROR_NEGATIVE_INVOICE = 'ERROR_NEGATIVE_INVOICE',
    ERROR_NO_INVOICE_LINE = 'ERROR_NO_INVOICE_LINE',
    ERROR_INVOICE_AMOUNTS_DO_NOT_MATCH = 'ERROR_INVOICE_AMOUNTS_DO_NOT_MATCH',
    ERROR_LINE_AMOUNTS_DO_NOT_MATCH = 'ERROR_LINE_AMOUNTS_DO_NOT_MATCH',
    ERROR_TAX_RATE_NOT_CORRESPONDING = 'ERROR_TAX_RATE_NOT_CORRESPONDING',
    ERROR_INVALID_BODY = 'ERROR_INVALID_BODY',
    ERROR_INVALID_LINE_ITEMS = 'ERROR_INVALID_LINE_ITEMS',
}

export interface BookingError {
    name: string;
    errors: string[];
    errorCode?: string;
}

export interface ParsedBookingErrors {
    errors: BookingError[];
    generalMessage?: string;
    errorCode?: string;
    hasLineItemsError?: boolean;
    hasRelationError?: boolean;
}

interface BackendErrorDetail {
    message: string;
    errors: Array<{ loc?: string[] }>;
    error_code: string | null;
}

export interface BookingErrorResponse {
    detail: BackendErrorDetail;
}

const fieldMapping: Record<string, string> = {
    'relation': 'relation_bookkeeping',
    'relation_iban': 'relation_iban',
    'vat_number': 'vat_number',
    'line_items_vat': 'line_items',
    'items': 'line_items',
    'items.0.amount': 'line_items',
    'items.0': 'line_items',
    'invoice_number': 'reference_identifier',
    'due_date': 'normalized_due_date',
    'invoice_date': 'normalized_date',
    'vat': 'vat_number',
    'amount': 'total_amount_payable_signed_amount',
    'vat_amount_overall': 'vat_amount_overall_signed_amount',
};

const errorCodeToFieldMapping: Record<string, string> = {
    [DocumentErrorCode.ERROR_MISSING_INVOICE_NUMBER]: 'reference_identifier',
    [DocumentErrorCode.ERROR_INVALID_DUE_DATE]: 'normalized_due_date',
    [DocumentErrorCode.ERROR_INVALID_INVOICE_DATE]: 'normalized_date',
    [DocumentErrorCode.ERROR_INVALID_VAT_FORMAT]: 'vat_number',
    [DocumentErrorCode.ERROR_INVALID_FIELD_FORMAT_INVOICE_NUMBER]: 'reference_identifier',
    [DocumentErrorCode.ERROR_ZERO_LINE]: 'line_items',
    [DocumentErrorCode.ERROR_ZERO_INVOICE]: 'total_amount_payable_signed_amount',
    [DocumentErrorCode.ERROR_NEGATIVE_INVOICE]: 'total_amount_payable_signed_amount',
    [DocumentErrorCode.ERROR_NO_INVOICE_LINE]: 'line_items',
    [DocumentErrorCode.ERROR_INVOICE_AMOUNTS_DO_NOT_MATCH]: 'total_amount_payable_signed_amount',
    [DocumentErrorCode.ERROR_LINE_AMOUNTS_DO_NOT_MATCH]: 'line_items',
    [DocumentErrorCode.ERROR_TAX_RATE_NOT_CORRESPONDING]: 'line_items',
    [DocumentErrorCode.ERROR_INVALID_LINE_ITEMS]: 'line_items',
};

function mapErrorLocationToField(loc: string[], errorCode?: string): string {
    if (errorCode && errorCodeToFieldMapping[errorCode]) {
        return errorCodeToFieldMapping[errorCode];
    }

    if (loc.length === 0) {
        return '';
    }

    const firstLoc = loc[0];
    if (fieldMapping[firstLoc]) {
        return fieldMapping[firstLoc];
    }

    if (loc.length > 1 && loc[0] === 'body') {
        const fieldPath = loc.slice(1).join('.');
        return fieldMapping[fieldPath] || fieldPath;
    }

    return firstLoc;
}

export function parseBookingErrors(error: BookingErrorResponse): ParsedBookingErrors {
    const detail = error?.detail;
    
    if (!detail) {
        return { errors: [] };
    }

    const result: ParsedBookingErrors = {
        errors: [],
        generalMessage: detail.message,
        errorCode: detail.error_code || undefined,
        hasLineItemsError: false,
        hasRelationError: false,
    };

    if (detail.errors && detail.errors.length > 0) {
        result.errors = detail.errors.flatMap(err => {

            if (err?.loc?.length > 1 && err.loc[0] !== 'body') {
                return err.loc.map(fieldName => ({
                    name: fieldMapping[fieldName] || fieldName,
                    errors: [detail.message],
                    errorCode: detail.error_code || undefined,
                }));
            } else {
                return [{
                    name: mapErrorLocationToField(err?.loc, detail.error_code || undefined),
                    errors: [detail.message],
                    errorCode: detail.error_code || undefined,
                }];
            }
        }).filter(err => err.name !== '');

        result.hasLineItemsError = result.errors.some(err => err.name === 'line_items');
        result.hasRelationError = result.errors.some(err => err.name === 'relation_bookkeeping');
    }

    return result;
}




